// components/stories/SuccessStories.jsx
// Updated: Queries real feedback from platformAccountability
import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { ChevronLeft, ChevronRight, Star, Send, CheckCircle, Users, Loader } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// GraphQL query for real feedback data
const GET_PLATFORM_FEEDBACK = gql`
  query PlatformAccountability {
    platformAccountability {
      recentFeedback {
        id
        rating
        comment
        category
        userName
        createdAt
      }
      avgRating
      totalFeedbackReceived
    }
  }
`;

const SuccessStories = () => {
  const { user, isLoggedIn } = useAuth();
  
  const [currentStory, setCurrentStory] = useState(0);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    headline: '',
    story: '',
    primaryBenefit: '',
    amountSaved: '',
    tokensEarned: ''
  });
  
  // Query real feedback from backend
  const { data, loading, error } = useQuery(GET_PLATFORM_FEEDBACK, {
    fetchPolicy: 'cache-and-network'
  });
  
  // Transform backend feedback to story format
  const approvedStories = (data?.platformAccountability?.recentFeedback || [])
    .filter(fb => fb.comment && fb.comment.trim() !== '')
    .map(fb => ({
      id: fb.id,
      userName: fb.userName || 'Member',
      headline: getCategoryHeadline(fb.category, fb.rating),
      story: fb.comment,
      rating: fb.rating,
      primaryBenefit: formatCategory(fb.category),
      metrics: null,
      createdAt: fb.createdAt
    }));
  
  const handleSubmitStory = async (e) => {
    e.preventDefault();
    
    if (!user) return;
    
    const newStory = {
      id: `story_${Date.now()}`,
      userId: user.id,
      userName: user.name?.split(' ')[0] || 'Member',
      headline: formData.headline,
      story: formData.story,
      primaryBenefit: formData.primaryBenefit,
      metrics: {
        saved: formData.amountSaved ? `$${formData.amountSaved}` : null,
        tokensEarned: formData.tokensEarned || null
      },
      submittedAt: new Date().toISOString(),
      status: 'pending' // Requires admin approval
    };
    
    // TODO: Replace with actual API call to submit for approval
    // For now, save to localStorage as pending
    const pendingStories = JSON.parse(localStorage.getItem('pending_success_stories') || '[]');
    pendingStories.push(newStory);
    localStorage.setItem('pending_success_stories', JSON.stringify(pendingStories));
    
    setSubmitSuccess(true);
    setFormData({
      headline: '',
      story: '',
      primaryBenefit: '',
      amountSaved: '',
      tokensEarned: ''
    });
    
    // Reset success message after 5 seconds
    setTimeout(() => {
      setSubmitSuccess(false);
      setShowSubmitForm(false);
    }, 5000);
  };
  
  // Loading state
  if (loading && !data) {
    return (
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 text-indigo-600 animate-spin" />
          <span className="ml-2 text-gray-600">Loading member stories...</span>
        </div>
      </div>
    );
  }
  
  // Error state - show empty state gracefully
  if (error) {
    console.error('Error loading stories:', error);
  }
  
  // No approved stories yet - show empty state
  if (approvedStories.length === 0) {
    return (
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6">
        <div className="text-center">
          {/* Empty State Icon */}
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-indigo-400" />
          </div>
          
          <h3 className="font-semibold text-gray-800 mb-2">Member Stories Coming Soon</h3>
          <p className="text-gray-600 text-sm mb-6">
            Be the first to share how UniLedge has helped you save money and earn in your community!
          </p>
          
          {/* Submit Story CTA */}
          {isLoggedIn ? (
            <>
              {!showSubmitForm ? (
                <button
                  onClick={() => setShowSubmitForm(true)}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                >
                  Share Your Story
                </button>
              ) : (
                <StorySubmitForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleSubmitStory}
                  onCancel={() => setShowSubmitForm(false)}
                  submitSuccess={submitSuccess}
                />
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">
              <button 
                onClick={() => {/* Navigate to login */}}
                className="text-indigo-600 hover:underline"
              >
                Sign in
              </button>
              {' '}to share your success story
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // Has approved stories - show carousel
  const story = approvedStories[currentStory];
  
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-800">Member Stories</h3>
          <p className="text-xs text-gray-500">
            {data?.platformAccountability?.totalFeedbackReceived || 0} reviews from real members
          </p>
        </div>
        {isLoggedIn && (
          <button
            onClick={() => setShowSubmitForm(true)}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            Share Yours →
          </button>
        )}
      </div>
      
      {/* Submit Form Modal */}
      {showSubmitForm && (
        <div className="mb-4">
          <StorySubmitForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmitStory}
            onCancel={() => setShowSubmitForm(false)}
            submitSuccess={submitSuccess}
          />
        </div>
      )}
      
      {/* Story Card */}
      {!showSubmitForm && (
        <>
          <div className="bg-white rounded-lg p-5 shadow-sm">
            {/* Header with Avatar */}
            <div className="flex items-center mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                {story.userName?.charAt(0) || 'M'}
              </div>
              <div className="ml-3">
                <p className="font-medium text-gray-800">{story.userName}</p>
                <p className="text-sm text-gray-500">UniLedge Member</p>
              </div>
              <div className="ml-auto flex">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-4 h-4 ${
                      i < (story.rating || 5) 
                        ? 'text-yellow-400 fill-current' 
                        : 'text-gray-300'
                    }`} 
                  />
                ))}
              </div>
            </div>
            
            {/* Headline */}
            {story.headline && (
              <h4 className="font-semibold text-gray-800 mb-2">"{story.headline}"</h4>
            )}
            
            {/* Story Text */}
            <p className="text-gray-600 italic mb-4">"{story.story}"</p>
            
            {/* Metrics */}
            {(story.metrics?.saved || story.metrics?.tokensEarned) && (
              <div className="flex gap-4 mb-3">
                {story.metrics.saved && (
                  <div className="bg-green-50 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium text-green-700">
                      Saved {story.metrics.saved}
                    </span>
                  </div>
                )}
                {story.metrics.tokensEarned && (
                  <div className="bg-yellow-50 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium text-yellow-700">
                      Earned {story.metrics.tokensEarned} tokens
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {/* Primary Benefit Tag */}
            {story.primaryBenefit && (
              <div className="bg-indigo-100 rounded-lg p-2 text-center">
                <span className="text-sm font-medium text-indigo-800">
                  {story.primaryBenefit}
                </span>
              </div>
            )}
            
            {/* Timestamp */}
            {story.createdAt && (
              <p className="text-xs text-gray-400 mt-3 text-right">
                {formatTimeAgo(story.createdAt)}
              </p>
            )}
          </div>
          
          {/* Navigation */}
          {approvedStories.length > 1 && (
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setCurrentStory(prev => 
                  prev === 0 ? approvedStories.length - 1 : prev - 1
                )}
                className="p-2 hover:bg-white rounded-full transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              
              <div className="flex space-x-2">
                {approvedStories.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStory(index)}
                    className={`h-2 rounded-full transition-all ${
                      currentStory === index
                        ? 'w-6 bg-indigo-600'
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
              
              <button
                onClick={() => setCurrentStory(prev => 
                  prev === approvedStories.length - 1 ? 0 : prev + 1
                )}
                className="p-2 hover:bg-white rounded-full transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Helper function to generate headline from category and rating
function getCategoryHeadline(category, rating) {
  const headlines = {
    'Product Quality': rating >= 4 ? 'Great products at amazing prices!' : 'Good value for money',
    'Delivery': rating >= 4 ? 'Fast and reliable delivery!' : 'Convenient pickup options',
    'App Experience': rating >= 4 ? 'So easy to use!' : 'Simple and straightforward',
    'Customer Service': rating >= 4 ? 'Amazing support team!' : 'Helpful customer service',
    'General': rating >= 4 ? 'Love UniLedge!' : 'Great community platform',
    'general': rating >= 4 ? 'Love UniLedge!' : 'Great community platform'
  };
  return headlines[category] || headlines['General'];
}

// Helper function to format category for display
function formatCategory(category) {
  const categoryMap = {
    'Product Quality': 'Quality Products',
    'Delivery': 'Convenient Delivery',
    'App Experience': 'Easy to Use',
    'Customer Service': 'Great Support',
    'General': 'Community Connection',
    'general': 'Community Connection',
    'product': 'Quality Products',
    'delivery': 'Convenient Delivery',
    'app': 'Easy to Use',
    'service': 'Great Support'
  };
  return categoryMap[category] || 'Community Connection';
}

// Helper function to format time ago
function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
}

// Story Submission Form Component
const StorySubmitForm = ({ formData, setFormData, onSubmit, onCancel, submitSuccess }) => {
  if (submitSuccess) {
    return (
      <div className="bg-white rounded-lg p-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h4 className="font-semibold text-gray-800 mb-2">Thank You!</h4>
        <p className="text-gray-600 text-sm">
          Your story has been submitted for review. We'll notify you once it's approved.
        </p>
      </div>
    );
  }
  
  return (
    <form onSubmit={onSubmit} className="bg-white rounded-lg p-4 space-y-4">
      <h4 className="font-semibold text-gray-800">Share Your UniLedge Story</h4>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Headline (optional)
        </label>
        <input
          type="text"
          value={formData.headline}
          onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
          placeholder="e.g., 'Finally saving money on groceries!'"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          maxLength={100}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Your Story *
        </label>
        <textarea
          value={formData.story}
          onChange={(e) => setFormData(prev => ({ ...prev, story: e.target.value }))}
          placeholder="Tell us how UniLedge has helped you..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          rows={4}
          required
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-1">{formData.story.length}/500 characters</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Primary Benefit
        </label>
        <select
          value={formData.primaryBenefit}
          onChange={(e) => setFormData(prev => ({ ...prev, primaryBenefit: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="">Select a benefit...</option>
          <option value="Saving Money">Saving Money</option>
          <option value="Earning Extra Income">Earning Extra Income</option>
          <option value="Community Connection">Community Connection</option>
          <option value="Quality Products">Quality Products</option>
          <option value="Convenience">Convenience</option>
        </select>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount Saved (optional)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              value={formData.amountSaved}
              onChange={(e) => setFormData(prev => ({ ...prev, amountSaved: e.target.value }))}
              placeholder="0"
              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min="0"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tokens Earned (optional)
          </label>
          <input
            type="number"
            value={formData.tokensEarned}
            onChange={(e) => setFormData(prev => ({ ...prev, tokensEarned: e.target.value }))}
            placeholder="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            min="0"
          />
        </div>
      </div>
      
      <p className="text-xs text-gray-500">
        By submitting, you agree to let UniLedge share your story (first name only) after review.
      </p>
      
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
        >
          <Send className="w-4 h-4 mr-2" />
          Submit
        </button>
      </div>
    </form>
  );
};

export default SuccessStories;